import React, { useState, useMemo } from 'react';
import { BarChart3, Download, DollarSign, Calendar } from 'lucide-react';
import { useData } from '../context/DataContext';
import { toast } from 'sonner';

import { useT } from '../context/LanguageContext';
const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

export default function Rapportage() {

  const { t } = useT();  const { betalingen, bonnen, handelAutos, stats } = useData();
  const [periode, setPeriode] = useState('jaar');

  const omzetPerMaand = stats.omzetPerMaand || [];
  const max = Math.max(1, ...omzetPerMaand.map(o => o.omzet || 0));

  // Filter bonnen + betalingen op periode
  const now = new Date();
  const [from, to] = useMemo(() => {
    const d = new Date(now);
    if (periode === 'maand') return [new Date(d.getFullYear(), d.getMonth(), 1), d];
    if (periode === 'kwartaal') {
      const q = Math.floor(d.getMonth() / 3) * 3;
      return [new Date(d.getFullYear(), q, 1), d];
    }
    if (periode === 'jaar') return [new Date(d.getFullYear(), 0, 1), d];
    return [new Date(0), d];
  }, [periode, now]);

  const inRange = (d) => { try { const x = new Date(d); return x >= from && x <= to; } catch { return false; } };

  const omzetBonnen = bonnen.filter(b => (b.type === 'Factuur' || b.type === 'Werkbon') && inRange(b.datum));
  const totaalOmzet = omzetBonnen.reduce((s, b) => s + (b.totaal || 0), 0);

  const betalingenInRange = betalingen.filter(b => inRange(b.datum || b.createdAt));
  const totaalBetalingen = betalingenInRange.reduce((s, b) => s + (b.bedrag || 0), 0);

  const verkoopAutos = handelAutos.filter(a => a.status === 'Verkocht');
  const verkoopMarge = verkoopAutos.reduce((s, a) => s + ((a.verkoopPrijs || 0) - (a.inkoopPrijs || 0)), 0);

  const noData = omzetPerMaand.every(o => (o.omzet || 0) === 0);

  const periodeLabels = {
    maand: t('Deze maand', 'Deze maand'),
    kwartaal: t('Dit kwartaal', 'Dit kwartaal'),
    jaar: t('Dit jaar', 'Dit jaar'),
    aangepast: t('Aangepast', 'Aangepast'),
  };

  return (
    <div data-testid="rapportage-page">
      <div className="cs-page-header">
        <div><h2>{t('page.reporting.title')}</h2><p>{t('page.reporting.sub')}</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button data-testid="btn-export" className="cs-btn primary" onClick={() => {
            // Echte CSV download (Excel opent .csv direct)
            const rows = [
              ['Type', 'Datum', 'Nummer', 'Klant', 'Kenteken', 'Subtotaal', 'Korting', 'BTW', 'Totaal', 'Betaald', 'Openstaand', 'Status'],
              ...omzetBonnen.map(b => [
                b.type || '', b.datum || '', b.nummer || '', b.klantNaam || '', b.kenteken || '',
                (b.subtotaal || 0).toFixed(2),
                (b.kortingBedrag || 0).toFixed(2),
                (b.btw || 0).toFixed(2),
                (b.totaal || 0).toFixed(2),
                (b.betaald || 0).toFixed(2),
                (b.openstaand || 0).toFixed(2),
                b.status || '',
              ]),
              [],
              ['Betalingen', '', '', '', '', '', '', '', '', '', '', ''],
              ['Datum', 'Bonnummer', 'Klant', 'Methode', 'Bedrag', 'Fooi', '', '', '', '', '', ''],
              ...betalingenInRange.map(p => [
                p.datum || '', p.bonNummer || '', p.klantNaam || '', p.methode || '',
                (p.bedrag || 0).toFixed(2), (p.fooi || 0).toFixed(2), '', '', '', '', '', '',
              ]),
              [],
              ['Totalen', '', '', '', '', '', '', '', totaalOmzet.toFixed(2), totaalBetalingen.toFixed(2), '', ''],
            ];
            const csv = rows.map(r => r.map(c => {
              const s = String(c ?? '');
              return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            }).join(';')).join('\n');
            const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const periodeLabel = periode || 'export';
            a.href = url; a.download = `monteurcentraal-rapport-${periodeLabel}-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click(); URL.revokeObjectURL(url);
            toast.success(`${t('Rapport geëxporteerd', 'Rapport geëxporteerd')} (${omzetBonnen.length} ${t('bonnen', 'bonnen')}, ${betalingenInRange.length} ${t('betalingen', 'betalingen')})`);
          }}><Download size={14} /> {t('Exporteren (CSV)', 'Exporteren (CSV)')}</button>
        </div>
      </div>

      <div className="cs-toolbar">
        <div style={{ display: 'flex', gap: 4, background: '#fff', border: '1px solid var(--cs-border)', borderRadius: 6, padding: 2 }}>
          {[{ k: 'maand' }, { k: 'kwartaal' }, { k: 'jaar' }, { k: 'aangepast' }].map(p => (
            <button data-testid={`period-${p.k}`} key={p.k} className="cs-btn ghost" style={{ padding: '4px 12px', background: periode === p.k ? 'var(--cs-accent-soft)' : 'transparent', color: periode === p.k ? 'var(--cs-accent)' : 'inherit' }} onClick={() => setPeriode(p.k)}>{periodeLabels[p.k]}</button>
          ))}
        </div>
      </div>

      <div className="cs-three-col" style={{ marginBottom: 14 }}>
        <div className="cs-kpi" data-testid="kpi-omzet"><div className="label">{t('Totaal omzet', 'Totaal omzet')}</div><div className="value">{eur(totaalOmzet)}</div></div>
        <div className="cs-kpi" data-testid="kpi-betalingen"><div className="label">{t('Ontvangen betalingen', 'Ontvangen betalingen')}</div><div className="value" style={{ color: 'var(--cs-green)' }}>{eur(totaalBetalingen)}</div></div>
        <div className="cs-kpi" data-testid="kpi-marge"><div className="label">{t('Auto handel marge', 'Auto handel marge')}</div><div className="value" style={{ color: 'var(--cs-green)' }}>{eur(verkoopMarge)}</div></div>
      </div>

      <div className="cs-two-col">
        <div className="cs-card padded">
          <h3 className="cs-card-title"><BarChart3 size={16} /> {t('dash.revenue_per_month')}</h3>
          {noData ? (
            <div className="cs-empty" style={{ padding: '24px 8px' }}>
              {t('Nog geen omzet geregistreerd. Zodra je facturen of werkbonnen aanmaakt verschijnt hier de grafiek.', 'Nog geen omzet geregistreerd. Zodra je facturen of werkbonnen aanmaakt verschijnt hier de grafiek.')}
            </div>
          ) : (
            <div className="cs-chart">
              {omzetPerMaand.map((m, i) => (
                <div key={`${m.jaar}-${m.maand}-${i}`} className="bar-wrap" title={eur(m.omzet)}>
                  <div className="bar" style={{ height: `${Math.max(2, (m.omzet / max) * 100)}%` }}></div>
                  <div className="label">{m.maand}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="cs-card padded">
          <h3 className="cs-card-title"><DollarSign size={16} /> {t('Top verkochte voertuigen', 'Top verkochte voertuigen')}</h3>
          <table className="cs-table">
            <thead><tr><th>{t('Auto')}</th><th style={{ textAlign: 'right' }}>{t('Inkoop')}</th><th style={{ textAlign: 'right' }}>{t('Verkoop')}</th><th style={{ textAlign: 'right' }}>{t('Marge')}</th></tr></thead>
            <tbody>
              {verkoopAutos.map(a => (
                <tr key={a.id}>
                  <td>{a.merk} {a.model}</td>
                  <td style={{ textAlign: 'right' }}>{eur(a.inkoopPrijs)}</td>
                  <td style={{ textAlign: 'right' }}>{eur(a.verkoopPrijs)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--cs-green)' }}>{eur((a.verkoopPrijs || 0) - (a.inkoopPrijs || 0))}</td>
                </tr>
              ))}
              {verkoopAutos.length === 0 && <tr><td colSpan={4} className="cs-empty">{t('Nog geen verkopen geregistreerd.', 'Nog geen verkopen geregistreerd.')}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="cs-card padded" style={{ marginTop: 14 }}>
        <h3 className="cs-card-title"><Calendar size={16} /> {t('Bonnen samenvatting', 'Bonnen samenvatting')} ({periodeLabels[periode]})</h3>
        <table className="cs-table">
          <thead><tr><th>{t('Type')}</th><th style={{ textAlign: 'right' }}>{t('Aantal')}</th><th style={{ textAlign: 'right' }}>{t('Totaal')}</th><th style={{ textAlign: 'right' }}>{t('Openstaand')}</th></tr></thead>
          <tbody>
            {['Werkbon', 'Offerte', 'Factuur'].map(tp => {
              const items = bonnen.filter(b => b.type === tp && inRange(b.datum));
              const totaal = items.reduce((s, i) => s + (i.totaal || 0), 0);
              const open = items.reduce((s, i) => s + (i.openstaand || 0), 0);
              return (
                <tr key={tp}>
                  <td><span className="cs-badge blue">{t(tp, tp)}</span></td>
                  <td style={{ textAlign: 'right' }}>{items.length}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{eur(totaal)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--cs-red)' }}>{eur(open)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
